import EventEmitter from 'events';
import Logger from '../Logger';
import Stopwatch from '../../utils/Stopwatch';
import MediaContentManager from './MediaContentManager';
import StreamCache from './MediaStreamCache';
import ValidationManagerWrapper from './ValidationManagerWrapper';
import MediaRecorderHelpers from './MediaRecorderHelpers';
import RecordingApi from './unified-recorder/recording/RecordingApi';
import RecordingApiError from './unified-recorder/recording/RecordingApiError';
import RecordingApiConfiguration from './unified-recorder/recording/RecordingApiConfiguration';
import OpentokRecording from './unified-recorder/OpentokRecording';
import validation from './unified-recorder/validation';

export default class MediaDeviceRecorder extends EventEmitter {
    /**
     * Creates MediaDeviceRecorder instance.
     * @param {ConfigurationManager} configurationManager entity to get configuration
     */
    constructor(configurationManager) {
        super();
        this.configurationManager = configurationManager;
        this.logger = new Logger('REC', configurationManager.isDebugMode());
        this.helpers = new MediaRecorderHelpers(this.configurationManager);
        this.config = Object.assign(
            {},
            MediaDeviceRecorder.defaults,
            (configurationManager.config || {}).recorderOptions,
        );

        this.logger.logDebug(`creating recorder with settings: ${JSON.stringify(this.config)}`);

        this.unifiedRecorder = {
            RecordingApiConfiguration,
            validation,
        };
        this.getRecordingApi = () => {
            if (!this.recordingApi) {
                return new Promise((resolve, reject) => {
                    this.logger.logDebug('started get RecordingAPI ');
                    this.logger.logDebug('started to detect environment');
                    this.logger.logInfo(`environment detected as:
                        + ${JSON.stringify(this.config.environmentDetectionResult)}`);
                    this.environment = this.configurationManager.config.environmentDetectionResult;
                    this.environment.isSafari = this.environment.browser.name.toLowerCase() === 'safari';
                    this.environment.isChrome = this.environment.browser.name.toLowerCase() === 'chrome';
                    this.environment.isMobile = this.environment.platform.type.toLowerCase() === 'mobile'
                        || this.environment.platform.type.toLowerCase() === 'tablet';

                    this.logger.logDebug(`isSafari: ${this.environment.isSafari},
                        isMobile: ${this.environment.isMobile}`);

                    // todo: that's just hotfix for safari. current OT solution can't record it
                    if (this.environment.isSafari && !this.environment.isMobile) {
                        this.config.recordAudio = false;
                    }

                    this.logger.logDebug(`settings after environment update: ${JSON.stringify(this.config)}`);

                    const opentokRecording = new OpentokRecording(this.config);
                    if (opentokRecording.check(this.environment)) {
                        opentokRecording.init().then(pluginContext => pluginContext.mediaDevices
                            .init().then(() => {
                                this.logger.logDebug('Recording API was received');
                                this.recordingApi = new RecordingApi(pluginContext);
                                resolve(this.recordingApi);
                            }, () => {
                                const error = new RecordingApiError('Failed to initialize the plugin');
                                this.logger.logError(`Recording API was not received, error: ${JSON.stringify(error)}`);
                                reject(error);
                            }));
                    }
                });
            }

            return Promise.resolve(this.recordingApi);
        };

        this.currentDevices = null;
        this.listOfStreams = new StreamCache();
        this.listOfDevices = {};
    }

    /**
     * Returns list of available devices
     * @return{Promise}
     */
    getAvailableDevices() {
        return new Promise((resolve, reject) => {
            this.getRecordingApi().then((api) => {
                this.logger.logDebug('start to get devices');
                api.mediaDevices.getDevices().then((devices) => {
                    this.logger.logDebug(`devices are received: ${JSON.stringify(devices)}`);
                    const result = {
                        audio: [],
                        video: [],
                    };

                    devices.forEach((device) => {
                        const devType = device.deviceType || device.type;
                        if (!result[devType]) {
                            result[devType] = [];
                        }

                        const dev = device;
                        if (this.listOfDevices[devType] && this.listOfDevices[devType][dev.id]) {
                            dev.permissionState = this.listOfDevices[devType][dev.id].permissionState;
                            dev.allowed = this.listOfDevices[devType][dev.id].allowed;
                            dev.denied = this.listOfDevices[devType][dev.id].denied;
                        }

                        dev.sequenceNo = result[devType].length;
                        result[devType].push(dev);
                    });

                    this.logger.logDebug(`devices are updated with permission info: ${JSON.stringify(result)}`);

                    resolve(result);
                }, reject);
            }, reject);
        });
    }

    /**
     * Returns list of currently used devices
     * @return{Promise<Array<Object.<Device>>>}
     */
    getCurrentDevices() {
        return Promise.resolve(this.currentDevices);
    }

    /**
     * switches to next device(s). If type is not provided then will switch all devices (video and audio)
     * @param{string} type, define if audio, video or both devices should be switched
     * @return {Promise<Object.<{
     *   video: Object.<Device>,
     *   audio: Object.<Device>
     * }>>}
     */
    switchToNextDevice(type) {
        return new Promise((resolve, reject) => {
            // if current devices is null then reject promise
            if (this.validationManager) {
                const err = new Error('device in use with testing. stop test and then switch device');
                this.logger.logError(err.message);
                reject(err);
                return;
            }

            if (this.recorder) {
                const err = new Error('device in use with recording. stop recording and then switch device');
                this.logger.logError(err.message);
                reject(err);
                return;
            }

            if (!this.currentDevices) {
                const err = new Error('Current devices were not defined first time via AskPermissions');
                this.logger.logError(err.message);
                reject(err);
                return;
            }

            const types = ['video', 'audio'].filter(i => i === type || !type);
            this.logger.logDebug(`Next devices will be switched: ${JSON.stringify(types)}`);
            this.getAvailableDevices().then((devices) => {
                types.forEach((deviceType) => {
                    if (this.currentDevices[deviceType]) {
                        const idx = (this.currentDevices[deviceType].sequenceNo + 1) % devices[deviceType].length;
                        if (!this.listOfDevices[deviceType]) {
                            this.listOfDevices[deviceType] = {};
                        }
                        this.currentStream = null;
                        const deviceId = this.currentDevices[deviceType].id;
                        this.listOfDevices[deviceType][deviceId] = this.currentDevices[deviceType];

                        this.logger.logDebug(`Switched from: ${JSON.stringify(this.currentDevices[deviceType])}`);
                        this.currentDevices[deviceType] = devices[deviceType][idx];
                        this.logger.logDebug(`to: ${JSON.stringify(this.currentDevices[deviceType])}`);

                        /* using previosly allowed stream */
                        this.currentStream = this.listOfStreams.getStream(this.currentDevices);
                    }
                });
                resolve(this.currentDevices);
            }, reject);
        });
    }

    /**
     * Returns only those devices which without permissions or stream was obsolete
     * @return {Promise<Array.<Object.<Device>>>}
     */
    getDevicesToAskPermissions() {
        return new Promise((resolve, reject) => {
            const devices = {
                array: [],
            };
            if (this.currentDevices) {
                const stream = this.listOfStreams.getStream(this.currentDevices);

                if (this.currentDevices.video && (!this.currentDevices.video.allowed || !stream)) {
                    devices.array.push(this.currentDevices.video);
                    devices.video = this.currentDevices.video;
                }

                if (this.currentDevices.audio && (!this.currentDevices.audio.allowed || !stream)) {
                    devices.array.push(this.currentDevices.audio);
                    devices.audio = this.currentDevices.audio;
                }
                resolve(devices);
            } else {
                const video = {
                    deviceType: 'video',
                };

                devices.array.push(video);
                devices.video = video;

                if (this.config.recordAudio) {
                    this.getAvailableDevices().then((availableDevices) => {
                        if (this.config.recordAudio && availableDevices.audio.length > 0) {
                            const audio = {
                                deviceType: 'audio',
                            };

                            devices.array.push(audio);
                            devices.audio = audio;
                        }
                        resolve(devices);
                    }, reject);
                } else {
                    resolve(devices);
                }
            }
        });
    }

    /**
     * Asks permissions for current devices
     * @return {Promise<Object.<{
     *   video: Object.<Device>,
     *   audio: Object.<Device>
     * }>>}
     */
    askPermissions() {
        /* private methods/variables section */
        const { permissionState } = MediaDeviceRecorder;

        const updateDevicesPermissionState = (devices, state, spend) => {
            devices.array.forEach((d) => {
                const device = d;
                device.permissionState = state;
                device.permissionReplyTime = spend;

                device.allowed = (state === permissionState.ALLOWED);
                device.denied = !device.allowed;

                if (spend <= this.config.autoReplyTime) {
                    if (device.allowed) {
                        device.permissionState = permissionState.AUTO_ALLOWED;
                    }

                    if (device.denied) {
                        device.permissionState = permissionState.AUTO_DENIED;
                    }
                }
            });

            const result = {
                currentDevices: this.currentDevices,
                requestedDevices: devices,
                responseTime: spend,
            };

            this.logger.logInfo(`permissions updated: ${JSON.stringify(result)}`);
            this.emit('permissionsUpdated', result);
        };

        const syncDevicesByName = (stream) => {
            if (!this.currentDevices) {
                this.logger.logDebug(`current devices initialized: ${JSON.stringify(this.currentDevices)}`);
                this.currentDevices = {};
                return new Promise((resolveSync, rejectSync) => {
                    this.getAvailableDevices().then((allDevices) => {
                        const idx = 0;
                        this.currentDevices.video = allDevices.video[idx];
                        if (this.config.recordAudio && allDevices.audio.length > idx) {
                            this.currentDevices.audio = allDevices.audio[idx];
                        }
                        if (stream) {
                            const gumStream = stream.getPluginStream().getGUMStream();
                            if (gumStream && gumStream.getVideoTracks) {
                                const vTracks = gumStream.getVideoTracks();
                                if (vTracks && vTracks.length > 0) {
                                    const cameraLabel = vTracks[0].label;
                                    const selectedDevices = allDevices.video.filter(cam => cam.name === cameraLabel);
                                    if (selectedDevices.length === 1) {
                                        [this.currentDevices.video] = selectedDevices;
                                    }
                                }
                                if (this.config.recordAudio) {
                                    const aTracks = gumStream.getAudioTracks();
                                    if (aTracks && aTracks.length > 0) {
                                        const micLabel = aTracks[0].label;
                                        const selectedDevices = allDevices.audio.filter(cam => cam.name === micLabel);
                                        if (selectedDevices.length === 1) {
                                            [this.currentDevices.audio] = selectedDevices;
                                        }
                                    }
                                }
                            }
                        }
                        resolveSync();
                    }, rejectSync);
                });
            }
            return Promise.resolve();
        };

        /* main logic of the method */
        return new Promise((resolve, reject) => {
            this.getDevicesToAskPermissions().then((devices) => {
                if (devices.array.length === 0) {
                    resolve(this.currentDevices);
                    return;
                }

                this.logger.logDebug(`Permissions will be asked for: ${JSON.stringify(devices)}`);
                this.getRecordingApi().then((api) => {
                    const stopwatch = new Stopwatch();
                    stopwatch.start();
                    this.logger.logInfo(`Start permission request for: ${JSON.stringify(devices)}`);
                    this.emit('permissionRequested', {
                        currentDevices: this.currentDevices,
                        requestedDevices: devices,
                    });

                    // todo: in positive and negetive positive cses current
                    // devices should be synced with selection if they were not defined
                    api.mediaDevices.getStream(devices.array).then((stream) => {
                        const spend = stopwatch.elapsed();
                        this.logger.logDebug(`stream received in ${spend}ms`);
                        this.logger.logDebug(stream);

                        syncDevicesByName(stream).then(() => {
                            this.listOfStreams.putStream(this.currentDevices, stream);
                            this.getDevicesToAskPermissions().then((devs) => {
                                this.helpers.isAllRequiredTracksInStreamAllowed(stream, devs).then((result) => {
                                    if (result.allowed) {
                                        this.currentStream = stream;
                                        this.logger.logDebug(`current stream was updated in ${stopwatch.elapsed()}ms`);
                                        this.logger.logDebug(stream);
                                        updateDevicesPermissionState(devs, permissionState.ALLOWED, spend);
                                        resolve(this.currentDevices);
                                    } else {
                                        updateDevicesPermissionState(devs, permissionState.DENIED, spend);
                                        const err = {
                                            error: new Error('Permissions denied for one of the track'),
                                            devices: result,
                                        };
                                        reject(err);
                                    }
                                }, reject);
                            }, reject);
                        }, reject);
                    }, (error) => {
                        const spend = stopwatch.elapsed();
                        this.logger.logError(`stream was not received in ${stopwatch.elapsed()}ms`);
                        this.logger.logError(error);
                        if (this.helpers.isErrorMeanWebcamBusy(error)) {
                            // allowed but can be busy with external programs
                            this.logger.logDebug('stream was not received, but we count it as allowed operation!');
                            syncDevicesByName().then(() => {
                                this.getDevicesToAskPermissions().then((devs) => {
                                    updateDevicesPermissionState(devs, permissionState.ALLOWED, spend);
                                    resolve(this.currentDevices);
                                }, reject);
                            }, reject);
                        } else {
                            updateDevicesPermissionState(devices, permissionState.DENIED, spend);
                            const err = {
                                error,
                                devices,
                            };

                            reject(err);
                        }
                    });
                }, reject);
            });
        });
    }

    /**
     * Starts test and validate stream of currently selected devices.
     * Put visual output into element defined by containerId and return video element as a result of Promise
     * @param{string} containerId defines element where visual output should be append
     * @param{Object.<{
     *    validationCheckInterval: int,
     *    validationRetryCount: int,
     *    validators: Object.<{
     *      activity: Object.<{
     *        checkInterval: int,
     *        }>,
     *      face: Object.<{
     *        checkInterval: int,
     *        }>,
     *      facePosition: Object.<{
     *        checkInterval: int,
     *        }>,
     *      light: Object.<{
     *        checkInterval: int,
     *        }>,
     *    }>,
     *  }>} options for the test and validation, if not provided then defaults will be used.
     *       If a validator is not defined, then it will not be applied for validation
     * @return{Promise<Node>}
     */
    startStreamValidation(containerId, options) {
        return new Promise((resolve, reject) => {
            if (!this.currentDevices || ((this.currentDevices.video && !this.currentDevices.video.allowed)
                    || (this.currentDevices.audio && !this.currentDevices.audio.allowed))) {
                const err = new Error('For one or more devices permissions were not granted!');
                reject(err);
                this.logger.logError(err);
                return;
            }

            if (this.validationManager) {
                const err = new Error('Test already has been started and was not stopped!');
                reject(err);
                this.logger.logError(err);
                return;
            }

            this.logger.logDebug(`start test for: ${JSON.stringify(this.currentDevices)}`);
            this.currentMediaOutputManager = new MediaContentManager(
                this.currentStream,
                this.outputElement,
                this.configurationManager,
            );
            this.currentMediaOutputManager.attachToContainer(containerId).then(
                () => {
                    this.logger.logDebug('Content was attached');
                },
                (e) => {
                    this.logger.logDebug(e);
                },
            );

            const testOptions = Object.assign({}, this.config.testOptions, options);
            testOptions.environment = this.environment;

            this.validationBusyTimeout = undefined;
            this.validationManager = new ValidationManagerWrapper(
                this.currentStream,
                testOptions,
                this.unifiedRecorder,
                this.configurationManager,
            );

            this.outputElement = this.currentMediaOutputManager.currentVideoOutput;

            this.validationManager.on('test', (results) => {
                if (results && results.deviceBusy && !this.validationBusyTimeout) {
                    this.validationBusyTimeout = setTimeout(() => {
                        this.currentDevices = this.helpers.cleanDevicesPermissions(this.currentDevices);

                        this.askPermissions().then(() => {
                            this.stopStreamValidation().then(() => {
                                this.startStreamValidation(containerId, options).then(resolve, reject);
                            }, reject);
                        }, (err) => {
                            this.logger.logError(err);
                            reject(err);
                        });
                    }, testOptions.busyDeviceValidationCheckInterval);
                }
                this.emit('test', results);
            });

            this.validationManager.start();
            this.logger.logDebug('validation started!');


            this.emit('testStarted', {
                currentDevices: this.currentDevices,
                output: this.outputElement,
            });
            resolve(this.outputElement);
        });
    }

    /**
     * Stops previously started stream validation, and remove visual output elements
     * @return{Promise}
     */
    stopStreamValidation() {
        if (this.validationManager) {
            this.logger.logDebug('test stopped');
            if (this.validationManager) {
                this.validationManager.stop();
                this.validationManager = null;
            }

            if (this.validationBusyTimeout) {
                clearTimeout(this.validationBusyTimeout);
                this.validationBusyTimeout = null;
            }

            this.currentMediaOutputManager.deattachFromAllContainers();
            this.emit('testStopped', { currentDevices: this.currentDevices });
            return Promise.resolve(true);
        }

        const err = new Error('Test was not started yet!');
        this.logger.logError(err);
        return Promise.reject(err);
    }

    /**
     * Starts recording from current devices. And return back id of the recording
     * @return {string}
     */
    startRecording() {
        return new Promise((resolve, reject) => {
            if (this.recorder) {
                const err = new Error('Recording already has been started and was not stopped!');
                reject(err);
                this.logger.logError(err);
                return;
            }

            this.logger.logInfo('started getting recording api');
            this.getRecordingApi().then((api) => {
                if (this.currentStream) {
                    this.logger.logDebug('stream exists!');
                    this.recorder = new api.Recorder(this.currentStream);
                    this.recorder.on('error', (error) => {
                        this.logger.logError(error);
                        reject(error);
                    });

                    this.logger.logDebug('trying to start recording');
                    this.recorder.start().then((data) => {
                        const eventData = {
                            currentDevices: this.currentDevices,
                            stream: this.currentStream,
                            data,
                        };
                        this.emit('recordStarted', eventData);
                        resolve(data);
                        this.logger.logInfo(`recording started: ${JSON.stringify(eventData)}`);
                    }, (err) => {
                        this.logger.logError(err);
                        reject(err);
                    });
                } else {
                    const err = new Error('Devices are busy or permissions were not granted');
                    this.logger.logError(err);
                    reject(err);
                }
            }, reject);
        });
    }

    /**
     * Stops previously launched recording
     * @return{Promise<boolean>}
     */
    stopRecording() {
        if (this.recorder) {
            return new Promise((resolve, reject) => {
                this.logger.logInfo('recording finishing');
                this.recorder.stop().then((data) => {
                    const streams = this.listOfStreams.getStreams();
                    streams.forEach((st) => {
                        st.stop();
                    });

                    this.listOfStreams.cleanAll();
                    this.emit('recordStopped', {
                        currentDevices: this.currentDevices,
                    });
                    this.recorder.originalRecorder.opentokService.stopSession().then(() => {
                        this.logger.logInfo('recording finished');
                        this.recorder = null;
                        this.currentStream = null;
                        resolve(data);
                    });
                }, (err) => {
                    this.logger.logError(err);
                    reject(err);
                    this.recorder = null;
                });
            });
        }
        const error = new Error('Recording was not started!');
        this.logger.logError(error);
        return Promise.reject(error);
    }
}

MediaDeviceRecorder.permissionState = {
    UNKNOWN: 0,
    ALLOWED: 1,
    AUTO_ALLOWED: 2,
    DENIED: 3,
    AUTO_DENIED: 4,
};

MediaDeviceRecorder.defaults = {
    autoReplyTime: 2000,
    recordAudio: false,
    opentokSdkUrl: 'https://static.opentok.com/v2/js/opentok.js',
    injectedApiUrl: 'https://devdc2.realeyesit.com',
    storage: {
        path: `s3://devresultdatabucket/local/${new Date().toISOString().split('T')[0]}`,
    },
    testOptions: {
        validationCheckInterval: 500,
        busyDeviceValidationCheckInterval: 10000,
        validationRetryCount: 5,
        validators: {
            activity: {
                checkInterval: 500,
            },
        },
    },
};
